import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import clsx from 'clsx';
import { mergeSx } from 'merge-sx';

import { FolderType, LocationDTO, OrganizationDTO } from '@/api/generated.ts';
import ChevronRightIcon from '@/assets/icons/chevron-right.svg?react';
import { borders } from '@/theme/constants.ts';
import typography from '@/theme/typography.ts';

import LocationItem from './LocationItem.tsx';
import LocationPathPopper from './LocationPathPopper.tsx';
import { SimpleItem } from './types.ts';

export interface LocationDisplayProps {
  location?: LocationDTO;
  organization?: OrganizationDTO;
  sx?: SxProps<Theme>;
  colorized?: boolean;
  useInsideDialog?: boolean;
  placeholder?: string | React.ReactNode;
  rootLinkTo?: string;
  outlined?: boolean;
  showFullTree?: boolean;
  newItem?: SimpleItem;
  maxItemWidth?: number;
}

const LocationDisplay: React.FC<LocationDisplayProps> = ({
  location,
  organization,
  sx,
  colorized,
  placeholder,
  rootLinkTo,
  outlined,
  useInsideDialog,
  newItem
}) => {
  const { t } = useTranslation('processes');

  const displayElements = useMemo(
    () =>
      [
        <LocationItem key={organization?.slug} type={FolderType.ORGANIZATION} name={organization?.name} />,
        location &&
          organization &&
          location?.parentType !== FolderType.ORGANIZATION &&
          location.slug !== organization.slug && (
            <LocationPathPopper
              useInsideDialog={useInsideDialog}
              locationSlug={location.slug!}
              organizationSlug={organization.slug!}
            >
              <Typography variant="body3">...</Typography>
            </LocationPathPopper>
          ),
        location?.slug !== organization?.slug && <LocationItem type={location?.type} name={location?.name} />,
        newItem?.name && <LocationItem type={newItem.type} name={newItem.name} className="LocationItem-draft" />
      ].filter(Boolean),
    [location, newItem, organization, useInsideDialog]
  );

  if (!displayElements.length) {
    return (
      placeholder || (
        <Typography
          sx={mergeSx<Theme>(
            {
              ...typography.body2,
              color: 'grey.700'
            },
            sx
          )}
        >
          {t('layouts.location.placeholder', { count: 1 })}
        </Typography>
      )
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={mergeSx<Theme>(
          {
            ...typography.body2,
            '& .LocationItem-draft-outer': {
              borderColor: 'primary.500',
              borderStyle: 'dashed'
            },
            '& .LocationItem-draft .LocationItem-icon': {
              color: 'primary.500'
            },
            '& .LocationItem-draft .LocationItem-label': {
              color: 'primary.500'
            }
          },
          useInsideDialog && {
            '& .LocationItem-label': {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              maxWidth: 70
            }
          },
          sx
        )}
      >
        {displayElements.map((node, index) => (
          <React.Fragment key={index}>
            <Box
              className={clsx(index === displayElements.length - 1 && newItem && 'LocationItem-draft-outer')}
              component={rootLinkTo && index === 0 ? Link : 'div'}
              to={rootLinkTo}
              sx={mergeSx(
                {
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  '& .LocationItem-icon': {
                    color: colorized ? 'primary.main' : 'grey.800'
                  },
                  '& .LocationItem-label': {
                    color: colorized ? 'primary.main' : 'text.primary'
                  }
                },
                index === displayElements.length - 1 &&
                  outlined && {
                    borderRadius: 40,
                    border: borders.default,
                    borderColor: 'grey.400',
                    py: '1px',
                    px: 1
                  },

                index === displayElements.length - 1 && {
                  '& .LocationItem-root .LocationItem-label': {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    maxWidth: 100
                  }
                }
              )}
            >
              {node}
            </Box>
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                color: colorized ? 'primary.500' : 'grey.700'
              }}
            >
              {index < displayElements.length - 1 && <ChevronRightIcon />}
            </Stack>
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

export default LocationDisplay;
